import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import "../../SpearParts/Layouts/Layouts.css";
import Select from "react-select";
import Axios from "../../../axios/axios";
import { toast } from "react-toastify";

const RntMiddle = ({ initialFilters = {} }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [selectedCategoryObj, setSelectedCategoryObj] = useState(null);

  // Filter states
  const [selectedCategory, setSelectedCategory] = useState(
    initialFilters.categoryId || ""
  );
  const [selectedBrand, setSelectedBrand] = useState(
    initialFilters.brandId || ""
  );
  const [selectedRentalType, setSelectedRentalType] = useState(
    initialFilters.rentalType || ""
  );

  // Filter brands based on selected category
  const filteredBrands = selectedCategory
    ? brands.filter((b) => String(b.categoryId?._id || b.categoryId) === String(selectedCategory))
    : brands;

  useEffect(() => {
    if (
      initialFilters &&
      (initialFilters.categoryId ||
        initialFilters.brandId ||
        initialFilters.rentalType)
    ) {
      setSelectedCategory(initialFilters.categoryId || "");
      setSelectedBrand(initialFilters.brandId || "");
      setSelectedRentalType(initialFilters.rentalType || "");
    }
  }, [initialFilters]);

  // Fetch categories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await Axios.get("/categories");
        const rentalCategories = res.data.filter((c) =>
          c.type.includes("rental")
        );
        setCategories(rentalCategories);
      } catch (error) {
        console.error("Error fetching categories", error);
      }
    };
    fetchCategories();
  }, []);

  // Fetch brands
  useEffect(() => {
    const fetchBrands = async () => {
      try {
        const res = await Axios.get("/type/rental");
        setBrands(res.data);
      } catch (error) {
        console.error("Error fetching brands", error);
      }
    };
    fetchBrands();
  }, []);

  // Search handler
  const handleSearch = async () => {
    if (!selectedRentalType && !selectedCategory && !selectedBrand) {
      toast.warning("Please select at least one filter.", {
        position: "bottom-center",
        autoClose: 5000,
        theme: "light",
      });
      return;
    }

    const filters = {};
    if (selectedRentalType) filters.rentalType = selectedRentalType;
    if (selectedCategory) filters.categoryId = selectedCategory;
    if (selectedBrand) filters.brandId = selectedBrand;

    try {
      setLoading(true);
      const res = await Axios.post(
        `/rental/search?${new URLSearchParams({
          ...filters,
          page: 1,
          limit: 9,
        }).toString()}`
      );

      const queryParams = new URLSearchParams(filters).toString();

      if (filters.categoryId) {
        // Check if category has subcategories
        if (selectedCategoryObj?.subcategories?.length > 0) {
          navigate(`/rental/subcategories?${queryParams}`, {
            state: { selectedFilters: filters },
          });
        } else {
          navigate(`/rental/products?${queryParams}`, {
            state: {
              rentals: res.data.data,
              selectedFilters: filters,
              pagination: res.data,
            },
          });
        }
      } else {
        navigate(`/rental/category?${queryParams}`, {
          state: {
            rentals: res.data.data,
            selectedFilters: filters,
            pagination: res.data,
          },
        });
      }
    } catch (error) {
      console.error("Error searching rentals:", error);
      toast.error("An error occurred while fetching rentals.", {
        position: "bottom-center",
        autoClose: 5000,
        theme: "light",
      });
    } finally {
      setLoading(false);
    }
  };

  // Shared select style (same as spare search)
  const selectStyles = {
    control: (provided) => ({
      ...provided,
      border: "1px solid #ced4da",
      boxShadow: "none",
      "&:hover": { borderColor: "#adb5bd" },
      minHeight: "38px",
      width: "100%",
    }),
    container: (provided) => ({
      ...provided,
      width: "100%",
    }),
    indicatorSeparator: () => ({ display: "none" }),
  };

  const hasSelectedFilters = !!(selectedCategory || selectedBrand || selectedRentalType);

  const handleClearFilters = () => {
    setSelectedCategory("");
    setSelectedBrand("");
    setSelectedRentalType("");
    setSelectedCategoryObj(null);
  };

  const sectionStyle = {
    backgroundSize: "cover",
    backgroundPosition: "center",
  };

  const style = {
    fontFamily: "Poppins', sans-serif",
    position: "relative",
    fontWeight: 1000,
    color: "#353C4B",
    fontSize: "104px !important",
  };

  const customStyles = {
    control: (base, state) => ({
      ...base,
      height: 50,
      minHeight: 50,
      borderRadius: 6,
      borderColor: "#ccc",
      backgroundColor: "#fff",
      paddingLeft: 4,
      fontFamily: "Poppins, sans-serif",
      fontSize: 15,
      boxShadow: state.isFocused ? "0 0 0 2px rgba(30,99,216,0.2)" : "none",
      "&:hover": { borderColor: "#ccc" },
    }),

    menu: (base) => ({
      ...base,
      marginTop: 2,
      borderRadius: 6,
      boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
      zIndex: 99999,
      backgroundColor: "#fff",
      position: "absolute",
    }),

    menuList: (base) => ({
      ...base,
      paddingTop: 0,
      paddingBottom: 0,
      maxHeight: 200,
    }),

    option: (base, state) => ({
      ...base,
      padding: "10px 12px",
      fontSize: 15,
      backgroundColor: state.isSelected
        ? "#efefef"
        : state.isFocused
          ? "#f3f4f6"
          : "#ffffff",
      color: "#374151",
      cursor: "pointer",
    }),

    placeholder: (base) => ({
      ...base,
      color: "#374151",
    }),

    dropdownIndicator: (base) => ({
      ...base,
      color: "#6b7280",
      paddingRight: 12,
    }),

    indicatorSeparator: () => ({
      display: "none",
    }),

    valueContainer: (base) => ({
      ...base,
      paddingLeft: 4,
    }),

    // This is crucial for proper z-index handling
    menuPortal: (base) => ({
      ...base,
      zIndex: 99999,
    }),
  };

  return (
    <div className="Middle-Container">
      <div className="Middle-Content">
        {/* Heading Section */}
        <div className="Middle-Heading-Section">
          <h1 className="Middle-Title">Find Rentals</h1>
          <p className="Middle-Description">
            Whether you're a professional enthusiast, finding the right items
            has never been easier. Use our precise search tool to locate the
            exact spare part you need. Search available rental items by type and
            availability status.
          </p>
        </div>

        {/* Search Form */}
        <div className="Middle-Search-Form">
          <div className="Middle-Select-Wrapper">
            <Select
              options={categories.map((cat) => ({
                value: cat._id,
                label: cat.name,
              }))}
              value={
                categories
                  .map((cat) => ({ value: cat._id, label: cat.name }))
                  .find((c) => c.value === selectedCategory) || null
              }
              onChange={(selectedOption) => {
                const categoryId = selectedOption ? selectedOption.value : "";
                setSelectedCategory(categoryId);
                setSelectedBrand(""); // Reset brand when category changes

                // Store complete category object so we can check subcategories later
                const fullCategory = categories.find(
                  (c) => c._id === categoryId
                );
                setSelectedCategoryObj(fullCategory || null);
              }}
              placeholder="Category"
              styles={customStyles}
              isSearchable isClearable
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
          </div>

          {/* Brand */}
          <div className="Middle-Select-Wrapper">
            <Select
              options={filteredBrands.map((brand) => ({
                value: brand._id,
                label: brand.name,
              }))}
              value={
                brands
                  .map((brand) => ({ value: brand._id, label: brand.name }))
                  .find((b) => b.value === selectedBrand) || null
              }
              onChange={(selectedOption) =>
                setSelectedBrand(selectedOption ? selectedOption.value : "")
              }
              placeholder="Brand"
              styles={customStyles}
              isSearchable isClearable
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
          </div>

          {/* Rental Type  */}
          <div className="Middle-Select-Wrapper">
            <Select
              options={[
                { value: "Tools", label: "Tools" },
                { value: "Machinery", label: "Machinery" },
                { value: "Vehicles", label: "Vehicles" },
                { value: "Construction", label: "Construction" },
                { value: "Electronics", label: "Electronics" },
              ]}
              value={
                [
                  { value: "Tools", label: "Tools" },
                  { value: "Machinery", label: "Machinery" },
                  { value: "Vehicles", label: "Vehicles" },
                  { value: "Construction", label: "Construction" },
                  { value: "Electronics", label: "Electronics" },
                ].find((r) => r.value === selectedRentalType) || null
              }
              onChange={(selectedOption) =>
                setSelectedRentalType(
                  selectedOption ? selectedOption.value : ""
                )
              }
              placeholder="Type"
              styles={customStyles}
              isSearchable isClearable
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
          </div>

          {/* Search Button */}
          <div className="Middle-Button-Wrapper" style={{ display: "flex", gap: "8px" }}>
            <button onClick={handleSearch} className="Middle-Search-Button">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="Middle-Search-Icon"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
              Search
            </button>
            {hasSelectedFilters && (
              <button
                type="button"
                onClick={handleClearFilters}
                className="Middle-Search-Button"
                style={{ backgroundColor: "#dc3545", borderColor: "#dc3545" }}
                title="Clear selected search filters"
              >
                ✕ Clear
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RntMiddle;
